import GridToolbar from "@/components/_Molecules/GridToolbar";
import DataGridClientPagination from "@/components/_Organisms/DataGridClientPagination";
import LoadingOverlay from "@/components/_Organisms/ProductList/components/LoadingOverlay";
import NoRowsOverlay from "@/components/_Organisms/ProductList/components/NoRowsOverlay";
import { usePersistedState } from "@/hooks";
import useProductsColumns from "@/hooks/useProductsColumns";
import { DataGrid, Error } from "@/includes";
import { RootState } from "@/services/redux/store";
import { CircularProgress, useTheme } from "@mui/material";
import { GridColumnVisibilityModel } from "@mui/x-data-grid";
import { useGridApiRef } from "@mui/x-data-grid-pro";
import { isEmpty } from "lodash";
import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useLocale } from "@/context/LocaleProvider";
import productsColumns from "./columns";
import { useTranslation } from "react-i18next";
import { TableProps } from "./types";

const Table = ({
  list,
  error,
  products,
  productsToShowWithData,
  loading,
  runningCollection,
  isGettingProductInfos,
  collectionResult,
}: TableProps) => {
  const { t } = useTranslation();
  const siteId = useSelector((state: RootState) => state.site.siteId);
  const currency = useSelector((state: RootState) => state.catalog.currency);
  const { locale } = useLocale();
  const theme = useTheme();
  const [currentPage, setCurrentPage] = React.useState(0);
  const [pageSize, setPageSize] = usePersistedState({
    key: "runPageSize-1",
    initialValue: 100,
  });
  const [datagridColumns, setDatagridColumns] = useState<Array<any>>([]);

  const columns = React.useMemo(
    () => productsColumns(theme, t, locale, currency, siteId),
    [theme]
  );
  const { getCatalogColumns, getInitialColumns, getAllColumns } =
    useProductsColumns(locale, currency);
  const catalogColumns = useSelector(
    (state: RootState) => state.catalog.catalogColumns
  );

  const apiRef = useGridApiRef();

  useEffect(() => {
    const additionnalsColumns = getCatalogColumns(catalogColumns, t);
    setDatagridColumns([...columns, ...additionnalsColumns]);
  }, [columns]);
  const [columnVisibilityModel, setColumnVisibilityModel] = usePersistedState({
    key: `productsTableVisibilityModel-preview-${siteId}`,
    initialValue: {},
  });

  const [initialData, setInitialData] = useState<Dic<any>>({});

  useEffect(() => {
    if (!isEmpty(datagridColumns)) {
      setInitialData(getInitialColumns(datagridColumns));
    }
  }, [datagridColumns]);

  useEffect(() => {
    if (isEmpty(columnVisibilityModel) && !isEmpty(initialData)) {
      setColumnVisibilityModel(initialData);
    } else if (!isEmpty(initialData)) {
      setColumnVisibilityModel({ ...initialData, ...columnVisibilityModel });
    }
  }, [initialData]);

  if (loading || runningCollection) {
    return <LoadingOverlay list={list} />;
  }
  if (!isEmpty(error)) return <Error errorMessage={error} />;
  if (products?.length === 0) return <NoRowsOverlay list={list} />;
  if (isEmpty(columnVisibilityModel)) return <CircularProgress size={20} />;
  return (
    <DataGrid
      paginationType="client"
      rowCount={productsToShowWithData?.length}
      rows={productsToShowWithData ?? []}
      loading={
        loading ||
        runningCollection ||
        isGettingProductInfos ||
        (!isEmpty(collectionResult) && isEmpty(productsToShowWithData))
      }
      pageSize={pageSize}
      currentPage={currentPage}
      columns={datagridColumns}
      componentsProps={{
        pagination: {
          totalItems: collectionResult.length,
          pageSize,
          handleChangePageSize: setPageSize,
          currentPage,
          handleChangeCurrentPage: setCurrentPage,
        },
        panel: {
          sx: {
            "& .MuiDataGrid-paper": {
              backgroundColor: "unset",
              background: theme.customPalette.others.layer_1,
            },
          },
        },
      }}
      components={{
        Pagination: DataGridClientPagination,
        Toolbar: GridToolbar,
      }}
      columnVisibilityModel={columnVisibilityModel}
      onColumnVisibilityModelChange={(model: GridColumnVisibilityModel) => {
        if (isEmpty(model)) {
          setColumnVisibilityModel(getAllColumns(columns));
        } else {
          setColumnVisibilityModel(model);
        }
      }}
      apiRef={apiRef}
    />
  );
};

export default Table;
